import { submitOrder } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import { OrderScoring, RequestDocsInfo } from 'models/api/order';
import { PreorderDetailsGeneralInfo } from 'models/api/preorder';
import {
    createContext,
    Dispatch,
    RefObject,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

type FormContextType = {
    isDirty: boolean;
    setDirty: Dispatch<SetStateAction<boolean>>;
    scoring: OrderScoring | null;
    setScoring: Dispatch<SetStateAction<OrderScoring | null>>;
    requestedDocs: RequestDocsInfo[];
    setRequestedDocs: Dispatch<SetStateAction<RequestDocsInfo[]>>;
    orderDetails: PreorderDetailsGeneralInfo | null;
    submitOrderMutation: UseMutationResult<any, unknown, string, unknown>;
    checksTabFormRef: RefObject<HTMLFormElement>;
};

const PreorderInfoContext = createContext<FormContextType>({} as FormContextType);

export type PreorderInfoProviderProps = {
    orderDetails: PreorderDetailsGeneralInfo | null;
    children: React.ReactNode;
    onSubmitOrderSuccess: () => void;
};

const PreorderInfoProvider = ({
    children,
    orderDetails,
    onSubmitOrderSuccess,
}: PreorderInfoProviderProps) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const [isDirty, setDirty] = useState(false);
    const [scoring, setScoring] = useState<OrderScoring | null>(null);
    const [requestedDocs, setRequestedDocs] = useState<RequestDocsInfo[]>([]);
    const [orderDetailsInfo, setOrderDetails] = useState<PreorderDetailsGeneralInfo | null>(
        orderDetails
    );
    const checksTabFormRef = useRef<HTMLFormElement>(null);

    useEffect(() => setOrderDetails(orderDetails), [orderDetails]);

    const submitOrderMutation = useMutation(
        'submitOrderMutation',
        (submitType: string) => {
            if (orderDetails) {
                return submitOrder(api, orderDetails.id, submitType, scoring);
            }
            return Promise.resolve(null);
        },
        {
            onSuccess: () => {
                if (onSubmitOrderSuccess) {
                    queryClient.invalidateQueries(['orderStatusCount']);
                    queryClient.invalidateQueries([['preorder']]);
                    onSubmitOrderSuccess();
                }
            },
        }
    );

    return (
        <PreorderInfoContext.Provider
            value={{
                isDirty,
                setDirty,
                scoring,
                setScoring,
                requestedDocs,
                setRequestedDocs,
                orderDetails: orderDetailsInfo,
                submitOrderMutation,
                checksTabFormRef,
            }}
        >
            {children}
        </PreorderInfoContext.Provider>
    );
};

const usePreorderInfoContext = () => useContext<FormContextType>(PreorderInfoContext);

export { PreorderInfoProvider, usePreorderInfoContext };
